import type { OperationVariables } from '@apollo/client';
import type { DocumentParameter } from '@vue/apollo-composable/dist/useQuery.js';
import type { BreadCrumb } from '~/components/common/types';
import { PageFetchError, isNotFoundError } from '~/helpers/fetchErrors';

type ReportedError = Error & { __sentryReported?: boolean };

type StrapiSlugPageData = {
  strapiContent: unknown | null;
  strapiTopBar: unknown;
  strapiCountdown: unknown;
  breadCrumbs: Array<BreadCrumb>;
};

/**
 * Shared useAsyncData flow for Strapi slug pages (content-page, landing-page):
 * Apollo fetch + validation, Sentry reporting, HTTP status, locale redirect.
 *
 * Callers supply query config and a transform callback for page-specific fields.
 */
export default async function useStrapiSlugPageData<
  TQuery,
  TVariables extends OperationVariables,
  TData extends StrapiSlugPageData,
>(options: {
  asyncDataKey: string;
  defaultData: () => TData;
  query: DocumentParameter<TQuery, TVariables>;
  variables: () => TVariables;
  getItems: (response: TQuery) => unknown[];
  pageLabel: string;
  slugRef: Ref<string>;
  transform: (item: unknown, response: TQuery) => TData;
}) {
  const { locale } = useI18n();
  const { $sentry } = useNuxtApp();

  const { refresh, data, error, pending, status } = await useAsyncData<TData>(
    options.asyncDataKey,
    async () => {
      try {
        const { error: apolloError, data: response } = await useLegacyApollo<
          TQuery,
          TVariables
        >(options.query, options.variables());

        if (apolloError != null || response == null) {
          throw new Error(PageFetchError.RESPONSE_ERROR, {
            cause: apolloError,
          });
        }

        const items = options.getItems(response);

        if (items.length === 0) {
          throw new Error(PageFetchError.NOT_FOUND);
        }

        if (items.length > 1) {
          $sentry.captureMessage(
            `${options.pageLabel} query returned ${items.length} results for slug ${options.slugRef.value}`,
          );
          const err: ReportedError = new Error(PageFetchError.RESPONSE_ERROR);
          err.__sentryReported = true;
          throw err;
        }

        return options.transform(items[0], response);
      } catch (e) {
        if (!isNotFoundError(e) && !(e as ReportedError).__sentryReported) {
          $sentry.captureException(e);
        }
        throw e;
      }
    },
    {
      default: options.defaultData,
    },
  );

  const isNotFound = computed(
    () =>
      (error.value != null && isNotFoundError(error.value)) ||
      (status.value === 'success' && data.value?.strapiContent == null),
  );

  const isError = computed(
    () => error.value != null && !isNotFoundError(error.value),
  );

  if (isNotFound.value && import.meta.server) {
    setResponseStatus(404);
  }

  if (isError.value && import.meta.server) {
    setResponseStatus(500);
  }

  if (data.value?.strapiContent != null) {
    const strapiContent = data.value.strapiContent as {
      localizedSlugs?: { locale: string; slug: string }[];
      locale?: string | null;
    };
    const { redirectToDefaultLocale } = useRedirectToLocaleSlugs(
      strapiContent.localizedSlugs,
      'index',
    );

    const sl = strapiContent.locale;
    if (sl != null && sl !== locale.value) {
      await redirectToDefaultLocale();
    }
  }

  const pageContent = computed(() => data.value.strapiContent);

  return {
    data,
    pageContent,
    refresh,
    pending,
    error,
    isNotFound,
    isError,
  };
}
